import { router } from "@inertiajs/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { CompanyLogo } from "@/components/CompanyLogo.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar.tsx";
import { bizminer_app_account_root_path } from "@/routes";
import type { BizminerAccountMinimal } from "@/types/serializers";

interface AccountSwitcherProps {
  accounts: BizminerAccountMinimal[];
  currentAccount: BizminerAccountMinimal | null;
}

export function AccountSwitcher({
  accounts,
  currentAccount,
}: AccountSwitcherProps) {
  if (!accounts || accounts.length === 0) {
    return null;
  }
  const handleAccountSwitch = (account_id: number) => {
    router.visit(bizminer_app_account_root_path({ account_id }), {
      preserveState: false,
    });
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <CompanyLogo
                logoUrl={currentAccount?.logo_url}
                name={currentAccount?.name}
                className="size-8 rounded-lg"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentAccount?.name || "Select Account"}
                </span>
                <span className="truncate text-xs">
                  {accounts.length}{" "}
                  {accounts.length === 1 ? "account" : "accounts"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Accounts
            </DropdownMenuLabel>
            {accounts.map((account) => (
              <DropdownMenuItem
                key={account.id}
                onClick={() => handleAccountSwitch(account.id)}
                className="gap-2 p-2"
              >
                <CompanyLogo
                  logoUrl={account.logo_url}
                  name={account.name}
                  className="size-6 rounded-sm"
                />
                <div className="flex-1">{account.name}</div>
                {currentAccount?.id === account.id && (
                  <Check className="size-4 ml-auto" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
